import { Box, ButtonProps } from "@mui/material";
import i18n from "i18next";
import {
    Dispatch,
    FC,
    isValidElement,
    ReactElement,
    SetStateAction,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import * as Icons from "../Icons";
import { InternalButtonLink } from "../InternalButtonLink";
import { QuestionnaireQueryType } from "../PensionQuestionnaire/Questionnaire.types";
import { HeadingS } from "../Typography";
import * as Styled from "./CustomStepper.styled";
import { ConfirmOverlayProps } from "./helpers";

const EmptyIcon = () => null;

export type BeforeNextFn = () => boolean | Promise<boolean>;

/* eslint-disable no-unused-vars */
type ShowConfirmModalFn = (props: ConfirmOverlayProps) => void;
type StepperContinueConditionSetter = (fn: BeforeNextFn) => void;
/* eslint-enable no-unused-vars */

export type DefaultStepProps = {
    setContinueCondition: StepperContinueConditionSetter;
    setNextButtonDisabled: Dispatch<SetStateAction<boolean>>;
    setNextButtonComponent?: (component: React.ReactElement<ButtonProps> | null) => void;
    hidePrevButton?: () => void;
    showConfirmModal: ShowConfirmModalFn;
    hideConfirmModal: () => void;
    width?: number;
    height?: number;
    setModalTitle?: (title: string | null) => void;
} & QuestionnaireQueryType;

type CustomStepperProps = {
    title: string;
    stepsCount: number;
    activeStepIndex: number;
    activeStepKey: string;
    // BREAKING: Expect only a component type (not an instantiated element). Pass e.g. MyStep, not <MyStep />.
    activeStep: FC<Partial<DefaultStepProps>>;
    onNext: () => void;
    onBack: () => void;
    onClose: () => void;
    overlay: ReactElement | null;
    showConfirmModal: ShowConfirmModalFn;
    hideConfirmModal: () => void;
    isLoading?: boolean;
    nextButtonText?: string;
    nextButtonLoadingText?: string;
    nextButtonDisabled?: boolean;
    externalBusy?: boolean;
    components?: {
        Stepper: React.JSXElementConstructor<unknown>;
        NextButton: React.JSXElementConstructor<ButtonProps>;
    };
};

export const defaultStepperComponents = {
    Stepper: Styled.CustomStepperStepperStack,
    NextButton: InternalButtonLink,
};

export const CustomStepper: FC<CustomStepperProps> = ({
    title,
    stepsCount,
    activeStepIndex,
    activeStepKey,
    activeStep,
    onNext,
    onBack,
    onClose,
    overlay,
    showConfirmModal,
    hideConfirmModal,
    isLoading = false,
    nextButtonText = i18n.t("global.continue"),
    nextButtonLoadingText,
    nextButtonDisabled = false,
    externalBusy = false,
    components = defaultStepperComponents,
}) => {
    const [nextDisabled, setNextDisabled] = useState(nextButtonDisabled);
    const [prevHidden, setPrevHidden] = useState(false);
    const [NextButtonComponent, setNextButtonComponent] = useState<React.ReactElement<ButtonProps> | null>(null);
    const { Stepper: StepperComponent, NextButton: NextButtonDefaultComponent } = components;
    const [customTitle, setCustomTitle] = useState<string | null>(null);

    const disabled = useMemo(
        () => ({
            next: isLoading || overlay !== null || nextDisabled || externalBusy,
            close: overlay !== null || isLoading,
            back: activeStepIndex === 0 || isLoading || overlay !== null,
        }),
        [isLoading, overlay, nextDisabled, externalBusy, activeStepIndex],
    );

    const stepIcons = useMemo(() => {
        return Array.from({ length: stepsCount }, () => {
            const key = crypto.randomUUID();
            return (
                <Styled.CustomStepperStepperStep key={key}>
                    <Styled.CustomStepperStepperStepLabel
                        StepIconComponent={EmptyIcon}
                    ></Styled.CustomStepperStepperStepLabel>
                </Styled.CustomStepperStepperStep>
            );
        });
    }, [stepsCount]);

    // Stores the validation function passed from the active step; useRef to avoid re-renders when updating
    const stepperContinueConditionRef = useRef<BeforeNextFn | null>(null);
    const isNavigatingRef = useRef(false);
    const stepChangeVersionRef = useRef(0);

    const contentRef = useRef<HTMLElement | null>(null);
    const [contentBoxSize, setContentBoxSize] = useState({
        width: 0,
        height: 0,
    });

    // Reset navigation lock when active step changes and bump step version.
    // Version check prevents stale async callbacks from advancing a newer step.
    useEffect(() => {
        stepChangeVersionRef.current = stepChangeVersionRef.current + 1;
        isNavigatingRef.current = false;
    }, [activeStepIndex, activeStepKey]);

    // Gets the step width and height
    useLayoutEffect(() => {
        if (contentRef.current)
            setContentBoxSize({
                width: contentRef.current.clientWidth,
                height: contentRef.current.clientHeight,
            });
    }, []);

    const onNextWithContinueCondition = useCallback(async () => {
        if (isNavigatingRef.current) return;
        isNavigatingRef.current = true;
        setNextDisabled(true);
        const stepVersionAtStart = stepChangeVersionRef.current;
        if (stepperContinueConditionRef.current !== null) {
            const ok = await stepperContinueConditionRef.current();
            if (!ok) {
                setNextDisabled(false);
                isNavigatingRef.current = false;
                return; // If the condition fails, do not proceed
            }
        }

        if (stepChangeVersionRef.current !== stepVersionAtStart) {
            setNextDisabled(false);
            isNavigatingRef.current = false;
            return;
        }

        setNextDisabled(false);
        onNext();
        stepperContinueConditionRef.current = null;
        if (contentRef.current) contentRef.current.scrollTo(0, 0);
    }, [onNext]);

    const nextButtonLabel = useMemo(() => {
        if (isLoading || externalBusy) {
            if (externalBusy && activeStepIndex === 0) {
                return <Styled.SmallCircularProgress />;
            }
            return nextButtonLoadingText ?? <Styled.SmallCircularProgress />;
        }
        return nextButtonText;
    }, [isLoading, externalBusy, nextButtonText, activeStepIndex, nextButtonLoadingText]);

    const hidePrevButton = useCallback(() => {
        setPrevHidden(true);
    }, []);

    const StepComponent = activeStep;

    return (
        <Styled.CustomStepperWrapperStack>
            {/* Overlay */}
            {overlay && <Styled.CustomStepperOverLayBox>{overlay}</Styled.CustomStepperOverLayBox>}

            {/* Header */}
            <Styled.CustomStepperHeaderStack>
                <Box>
                    <Styled.CustomStepperHeaderIconButton
                        onClick={() => {
                            stepperContinueConditionRef.current = null;
                            setNextDisabled(false);
                            onBack();
                        }}
                        disabled={disabled.back}
                        $hidden={prevHidden}
                    >
                        <Icons.Back />
                    </Styled.CustomStepperHeaderIconButton>
                </Box>
                <HeadingS strong>{customTitle ?? title}</HeadingS>
                <Box>
                    <Styled.CustomStepperHeaderIconButton onClick={onClose} disabled={disabled.close}>
                        <Icons.Close />
                    </Styled.CustomStepperHeaderIconButton>
                </Box>
            </Styled.CustomStepperHeaderStack>

            {/* Stepper */}
            <StepperComponent>
                <Styled.CustomStepperStepper activeStep={activeStepIndex}>{stepIcons}</Styled.CustomStepperStepper>
            </StepperComponent>

            {/* Active Step Content */}
            <Styled.CustomStepperActiveStepContentBox ref={contentRef}>
                <StepComponent
                    setContinueCondition={(fn: BeforeNextFn) => {
                        stepperContinueConditionRef.current = fn;
                    }}
                    setNextButtonDisabled={setNextDisabled}
                    showConfirmModal={showConfirmModal}
                    hideConfirmModal={hideConfirmModal}
                    hidePrevButton={hidePrevButton}
                    setNextButtonComponent={setNextButtonComponent}
                    width={contentBoxSize.width}
                    height={contentBoxSize.height}
                    setModalTitle={setCustomTitle}
                />
            </Styled.CustomStepperActiveStepContentBox>

            {/*Bottom Control next button */}
            <Styled.CustomStepperBottomControlStack>
                {isValidElement(NextButtonComponent) ? (
                    NextButtonComponent
                ) : (
                    <NextButtonDefaultComponent
                        component="button"
                        onClick={onNextWithContinueCondition}
                        variant="contained"
                        disabled={disabled.next}
                    >
                        {nextButtonLabel}
                    </NextButtonDefaultComponent>
                )}
            </Styled.CustomStepperBottomControlStack>
        </Styled.CustomStepperWrapperStack>
    );
};
